import qrcode from 'qrcode-generator';
import jsQR from 'jsqr';

export type Pattern = 'dots' | 'diamond' | 'bars';
export type RenderMode = 'standard' | 'custom' | 'art';
export interface ArtOptions { data: string; size: number; artwork?: string; pattern: Pattern; strength: number; }

export const utf8QrData = (data: string) => encodeURIComponent(data).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

export function createMatrix(data: string) {
    const qr = qrcode(0, 'H');
    // Pass UTF-8 bytes through the library's default byte encoder without changing global settings.
    const binary = utf8QrData(data);
    qr.addData(binary, 'Byte');
    qr.make();
    return qr;
}

export function loadArtwork(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Не удалось прочитать изображение. Выберите PNG, JPEG или WebP.'));
        image.src = source;
    });
}
export function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Не удалось сохранить PNG.')), 'image/png'));
}

export function decodeCanvas(canvas: HTMLCanvasElement, data: string) {
    const pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(pixels.data, canvas.width, canvas.height, { inversionAttempts: 'dontInvert' })?.data === data;
}

// Keep structural modules square; decoration never covers finders, timing or alignment patterns.
function structural(row: number, col: number, count: number) {
    if ((row <= 8 && col <= 8) || (row <= 8 && col >= count - 8) || (row >= count - 8 && col <= 8) || row === 6 || col === 6) return true;
    if (count >= 45 && ((row < 6 && col >= count - 11) || (col < 6 && row >= count - 11))) return true;
    const version = (count - 17) / 4;
    if (version < 2) return false;
    const number = Math.floor(version / 7) + 2;
    const step = version === 32 ? 26 : Math.ceil((version * 4 + number * 2 + 1) / (number * 2 - 2)) * 2;
    const centers = [6];
    for (let pos = count - 7; centers.length < number; pos -= step) centers.splice(1, 0, pos);
    return centers.some(y => Math.abs(row - y) <= 2 && centers.some(x => Math.abs(col - x) <= 2));
}

export async function renderArtQr({ data, size, artwork, pattern, strength }: ArtOptions) {
    let qr: ReturnType<typeof qrcode>;
    try { qr = createMatrix(data); } catch { throw new Error('Слишком много данных для QR. Сократите контакт или ссылку.'); }
    const count = qr.getModuleCount();
    if (size < (count + 8) * 4) throw new Error(`Для этого QR выберите размер не меньше ${(count + 8) * 4} px.`);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const cell = Math.floor(size / (count + 8));
    const side = count * cell;
    const origin = Math.floor((size - side) / 2);
    const image = artwork ? await loadArtwork(artwork) : null;
    const draw = (coverage: number) => {
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, size, size);
        if (image) {
            // Fit the whole illustration instead of cropping the user's subject.
            const scale = Math.min(side / image.naturalWidth, side / image.naturalHeight);
            const w = image.naturalWidth * scale, h = image.naturalHeight * scale;
            ctx.globalAlpha = strength / 100;
            ctx.drawImage(image, origin + (side - w) / 2, origin + (side - h) / 2, w, h);
            ctx.globalAlpha = 1;
        }
        for (let row = 0; row < count; row++) for (let col = 0; col < count; col++) {
            const x = origin + col * cell, y = origin + row * cell;
            const dark = qr.isDark(row, col);
            const fixed = structural(row, col, count);
            ctx.fillStyle = dark ? '#111111' : '#ffffff';
            if (fixed) { ctx.fillRect(x, y, cell, cell); continue; }
            if (!image && !dark) continue;
            const width = cell * coverage, pad = (cell - width) / 2;
            ctx.beginPath();
            if (image || pattern === 'dots') {
                ctx.arc(x + cell / 2, y + cell / 2, width / 2, 0, Math.PI * 2);
                ctx.fill();
            } else if (pattern === 'diamond') {
                ctx.moveTo(x + cell / 2, y + pad);
                ctx.lineTo(x + cell - pad, y + cell / 2);
                ctx.lineTo(x + cell / 2, y + cell - pad);
                ctx.lineTo(x + pad, y + cell / 2);
                ctx.closePath(); ctx.fill();
            } else ctx.fillRect(x + pad, y, width, cell);
        }
    };
    // Validate the exact export pixels, strengthening the modules if the image interferes.
    for (const coverage of image ? [0.62, 0.76, 0.9] : [0.85, 1]) {
        draw(coverage);
        if (decodeCanvas(canvas, data)) return { blob: await canvasBlob(canvas), strengthened: image && coverage > 0.62 };
    }
    throw new Error('Этот вариант не удалось считать. Увеличьте размер QR или уменьшите яркость изображения.');
}
